import React, { useState } from "react";
import styled from "styled-components";
import LastPageIcon from '@mui/icons-material/LastPage';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import NextPageIcon from '@mui/icons-material/KeyboardArrowRight';
import BackPageIcon from '@mui/icons-material/KeyboardArrowLeft';


const PaginationWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 15px;

  @media (max-width: 650px) {
    flex-direction: column;
    align-items: flex-end;
  }
`;

const PaginationButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const ControlsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;

  @media (max-width: 650px) {
    flex-direction: column;
    align-items: flex-end;
    gap: 10px;
  }
`;

const JumpWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;

  input {
    padding: 2px 2px !important;
    width: 30px !important;
  }
`;

const JumpInput = styled.input`
  width: 50px;
  padding: 4px 6px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
  color: var(--Text-Primary, #333);
  outline: none;
  text-align: center;

  &:focus {
    border-color: #999;
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;

const DisplayedRowsLabel = styled.div`
  font-size: 13px;
  color: var(--Text-Primary, #333);
  margin-left: 3px;
`;

const PageButton = styled.button`
  padding: 6px 4px;
  border: 0px solid transparent;
  background: transparent;
  color: var(--Text-Primary, #333);
  font-weight: ${(props) => (props.$active ? "bold" : "normal")};
  cursor: pointer;
  border-radius: 4px;
  font-size: 13px;
  transition: background 0.2s;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:hover:not(:disabled) {
    background: #f5f5f5;
  }

  &.icon {
    display: flex;
    align-items: center;

    &.double-icon {
      svg {
        margin-right: -5px;
        margin-left: -5px;
      }
    }
  }
`;

const Ellipsis = styled.span`
  padding: 6px 8px;
  color: var(--Text-Primary, #333);
  font-size: 13px;
  user-select: none;
`;

function TablePagination({ count, rowsPerPage, page, onPageChange, className, labelDisplayedRows }) {
  const [jumpPage, setJumpPage] = useState("");
  const totalPages = Math.ceil(count / rowsPerPage) || 1;
  const currentPage = page + 1; // UI representation uses 1-based indexing

  const from = count === 0 ? 0 : page * rowsPerPage + 1;
  const to = Math.min(count, (page + 1) * rowsPerPage);

  const handlePageClick = (newPage) => {
    if (newPage !== currentPage && newPage >= 1 && newPage <= totalPages) {
      onPageChange(null, newPage - 1); // Pass the 0-based page index to the handler
    }
  };

  const handleJump = () => {
    const pageNum = parseInt(jumpPage, 10);
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
      handlePageClick(pageNum);
      setJumpPage("");
    }
  };

  const handleJumpKeyDown = (e) => {
    if (e.key === "Enter") {
      handleJump();
    }
  };

  const getPageNumbers = () => {
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (currentPage <= 3) {
      return [1, 2, 3, 4, 5, "..."];
    }

    if (currentPage >= totalPages - 2) {
      return [
        "...",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }

    return [
      "...",
      currentPage - 2,
      currentPage - 1,
      currentPage,
      currentPage + 1,
      currentPage + 2,
      "...",
    ];
  };

  if (totalPages <= 1) return null;

  return (
    <PaginationWrapper className={className}>
      {labelDisplayedRows ? (
        <DisplayedRowsLabel>{labelDisplayedRows({ from, to, count })}</DisplayedRowsLabel>
      ) : (
        <div />
      )}
      <ControlsWrapper>
        <PaginationButtons>
          <PageButton className="icon" onClick={() => handlePageClick(1)} disabled={currentPage === 1}>
            <FirstPageIcon />
          </PageButton>
          <PageButton className="icon  double-icon" onClick={() => handlePageClick(Math.max(1, currentPage - 10))} disabled={currentPage === 1}>
            <BackPageIcon /><BackPageIcon />
          </PageButton>
    
          {getPageNumbers().map((p, index) =>
            p === "..." ? (
              <Ellipsis key={`ellipsis-${index}`}>...</Ellipsis>
            ) : (
              <PageButton key={p} $active={p === currentPage} onClick={() => handlePageClick(p)}>
                {p}
              </PageButton>
            )
          )}
    
          <PageButton className="icon double-icon" onClick={() => handlePageClick(Math.min(totalPages, currentPage + 10))} disabled={currentPage === totalPages}>
            <NextPageIcon /><NextPageIcon />
          </PageButton>
          <PageButton className="icon" onClick={() => handlePageClick(totalPages)} disabled={currentPage === totalPages}>
            <LastPageIcon />
            {currentPage === totalPages ? null :<div>{totalPages}</div>}
          </PageButton>
        </PaginationButtons>
        <JumpWrapper>
          <JumpInput
            type="number"
            min={1}
            max={totalPages}
            value={jumpPage}
            onChange={(e) => setJumpPage(e.target.value)}
            onKeyDown={handleJumpKeyDown}
          />
          <PageButton onClick={handleJump} disabled={!jumpPage}>Go</PageButton>
        </JumpWrapper>
      </ControlsWrapper>
    </PaginationWrapper>
  );
}

export default TablePagination;